import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { SortableTableHead } from "@/components/ui/sortable-table-head"
import { Pencil, Trash2 } from "lucide-react"
import { formatCNPJ, formatPhone } from "@/lib/masks"
import type { Company, CompanyFilters } from "@/types/company"

interface CompanyTableProps {
  data: Company[]
  filters: CompanyFilters
  onSort: (column: string) => void
  onEdit: (item: Company) => void
  onDelete: (item: Company) => void
  translationPrefix: string
}

export function CompanyTable({
  data,
  filters,
  onSort,
  onEdit,
  onDelete,
  translationPrefix,
}: CompanyTableProps) {
  const { t } = useTranslation()

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead
              label={t(`${translationPrefix}.nameLabel`)}
              sortKey="name"
              currentSortKey={filters.sort}
              direction={filters.direction}
              onSort={onSort}
            />
            <SortableTableHead
              label={t(`${translationPrefix}.taxIdLabel`)}
              sortKey="tax_id"
              currentSortKey={filters.sort}
              direction={filters.direction}
              onSort={onSort}
              className="hidden sm:table-cell"
            />
            <SortableTableHead
              label={t(`${translationPrefix}.taxNameLabel`)}
              sortKey="tax_name"
              currentSortKey={filters.sort}
              direction={filters.direction}
              onSort={onSort}
              className="hidden md:table-cell"
            />
            <SortableTableHead
              label={t(`${translationPrefix}.streetLabel`)}
              sortKey="street"
              currentSortKey={filters.sort}
              direction={filters.direction}
              onSort={onSort}
              className="hidden lg:table-cell"
            />
            <SortableTableHead
              label={t(`${translationPrefix}.cityStateLabel`)}
              sortKey="city"
              currentSortKey={filters.sort}
              direction={filters.direction}
              onSort={onSort}
              className="hidden lg:table-cell"
            />
            <SortableTableHead
              label={t("common.email")}
              sortKey="email"
              currentSortKey={filters.sort}
              direction={filters.direction}
              onSort={onSort}
              className="hidden xl:table-cell"
            />
            <SortableTableHead
              label={t(`${translationPrefix}.phoneLabel`)}
              sortKey="phone"
              currentSortKey={filters.sort}
              direction={filters.direction}
              onSort={onSort}
              className="hidden xl:table-cell"
            />
            <TableHead className="w-[100px]">{t("common.actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((item, index) => (
            <TableRow key={item.id} className={index % 2 === 1 ? "bg-muted" : ""}>
              <TableCell className="font-medium">{item.name}</TableCell>
              <TableCell className="hidden sm:table-cell">
                {item.tax_id ? formatCNPJ(item.tax_id) : "-"}
              </TableCell>
              <TableCell className="hidden md:table-cell">{item.tax_name || "-"}</TableCell>
              <TableCell className="hidden lg:table-cell">{item.street || "-"}</TableCell>
              <TableCell className="hidden lg:table-cell">
                {item.city && item.state ? `${item.city} - ${item.state}` : item.city || item.state || "-"}
              </TableCell>
              <TableCell className="hidden xl:table-cell">{item.email || "-"}</TableCell>
              <TableCell className="hidden xl:table-cell">
                {item.phone ? formatPhone(item.phone) : "-"}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEdit(item)}
                    title={t(`${translationPrefix}.edit${translationPrefix === "customers" ? "Customer" : "Supplier"}`)}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(item)}
                    className="text-destructive hover:text-destructive"
                    title={t(`${translationPrefix}.delete${translationPrefix === "customers" ? "Customer" : "Supplier"}`)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
